// Copyright (c) StrangeBee 2023

import { THButton } from '@Molecules/Inputs/THButton/THButton';
import React, { ReactElement, useMemo } from 'react';
import { CustomFieldOutput, CaseCustomField } from 'thehive-sdk';
import { useLocalMessage } from '@Helpers/locale';
import { classList } from '@Helpers/className';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import THSelector from '@Molecules/THSelector/THSelector';
import { THForm } from '@Molecules/THForm/THForm';
import useValidateForm from '@Hooks/useValidateForm';
import { ICONS } from '@Constants/iconsApp';
import { Space } from 'antd';
import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { MandatoryAsterisk } from '@Atoms/MandatoryAsterisk/MandatoryAsterisk';

import './CFValueItemAdd.scss';

interface CFValueItemFormValues {
    customFieldGrouped: any[][];
}

interface CFValueItemAddProps {
    customFieldList: CustomFieldOutput[];
    onAdd: (customField: Partial<CaseCustomField>) => void;
    className?: string;
}

export const CFValueItemAdd = ({ customFieldList, onAdd, className }: CFValueItemAddProps): ReactElement => {
    const i = useLocalMessage();
    const { form, isValid, onFormValuesChange, fieldsTouched, setFieldsTouched } = useValidateForm();

    const onFinish = ({ customFieldGrouped }: CFValueItemFormValues) => {
        customFieldGrouped.forEach((cfList) => {
            Object.values(cfList)
                .find(Boolean)
                ?.forEach(({ name, displayName, description, type, group }: any) =>
                    onAdd({
                        name,
                        displayName,
                        description,
                        type,
                        group,
                    }),
                );
        });
    };

    const groupList = useMemo(
        () =>
            customFieldList.reduce(
                (acc, { group = '' }) => (acc.includes(group) ? acc : [...acc, group]),
                [] as string[],
            ),
        [customFieldList],
    );

    const customFieldListTreated = useMemo(() => {
        const libCF = customFieldList.reduce<Record<string, CustomFieldOutput[]>>((acc, curr) => {
            const { group = '' } = curr;
            return {
                ...acc,
                [group]: [...(acc[group] || []), curr],
            };
        }, {});

        return Object.entries(libCF).reduce<Record<string, CustomFieldOutput[]>>(
            (acc, [group, values]) => ({
                ...acc,
                [group]: values.sort((a, b) => a.displayName?.localeCompare(b.displayName || '') || 0),
            }),
            {},
        );
    }, [customFieldList]);

    return (
        <THDrawer
            title={i('customField.value.add.drawer.title')}
            triggerElem={
                <THButton className={classList('cf-value-item-add__button', className)} type="link">
                    {i('general.add')}
                </THButton>
            }
            className="drawer-form"
            afterVisibleChange={() => form.resetFields()}
            disableClose={fieldsTouched}
        >
            {(toggleDrawer) => (
                <THForm<CFValueItemFormValues>
                    name="cf-value-add-form"
                    onFinish={(values) => {
                        onFinish(values);
                        toggleDrawer();
                    }}
                    form={form}
                    initialValues={{
                        customFieldGrouped: groupList.map(() => []),
                    }}
                    onValuesChange={onFormValuesChange}
                >
                    <THForm.Layout
                        footer={
                            <FormSubmitButton
                                onCancel={() => {
                                    toggleDrawer();
                                    form.resetFields();
                                    setFieldsTouched(false);
                                }}
                                disabled={!isValid}
                                formName="cf-value-add-form"
                            />
                        }
                    >
                        <THForm.List name="customFieldGrouped">
                            {(fields) =>
                                fields.map(({ name: index, key }) => (
                                    <THForm.Item
                                        label={groupList[index]}
                                        name={[index, groupList[index]]}
                                        fieldKey={key}
                                        key={key}
                                    >
                                        <THSelector<CustomFieldOutput>
                                            dataSource={customFieldListTreated[groupList[index]] || []}
                                            refKey="_id"
                                        >
                                            {({ displayName, type, mandatory }: CustomFieldOutput, selected) => (
                                                <div className="cf-value-item-add__display-name">
                                                    <Space>
                                                        {selected ? (
                                                            <ICONS.CheckCircleTwoTone />
                                                        ) : (
                                                            <ICONS.CheckCircleOutlined />
                                                        )}
                                                        <span>
                                                            {displayName}
                                                            {mandatory && <MandatoryAsterisk />}
                                                        </span>
                                                    </Space>
                                                    <span className="cf-value-item-add__display-name__type">
                                                        {type}
                                                    </span>
                                                </div>
                                            )}
                                        </THSelector>
                                    </THForm.Item>
                                ))
                            }
                        </THForm.List>
                    </THForm.Layout>
                </THForm>
            )}
        </THDrawer>
    );
};
